import { useMemo, useRef, useLayoutEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useUI, visibleHourRange } from '@/stores/ui';
import { Icon } from './icon';
import { cn } from '@/lib/cn';
import { APP_PALETTE, toneForLabel, labelFor, type AppTone } from './app-colors';
import type { ActivityWindow } from '@focus/shared';
import { NowLine } from './now-line';
import { SleepOverlay, useSleepSegments, type SleepSegment } from './sleep-overlay';

function isInSleep(hour: number, dayIdx: number, segs: SleepSegment[]): boolean {
  return segs.some((s) => s.dayIdx === dayIdx && hour >= s.sHour && hour < s.eHour);
}

// Adult content is filtered server-side at ingest (stored as "Privat" placeholder).
// This is a client-side belt-and-suspenders pass for any unredacted events.
const ADULT_RX = /(porn|xhamster|xvideos|eporner|redtube|xnxx|youporn|spankbang|chaturbate|stripchat|onlyfans|fapvid|fapster|fapello|nhentai|hentai|rule34|motherless|tnaflix|hclips|beeg|camsoda|bongacams|kinkvids|tube8|justporn|pornxp|sexyporn|pornoperso|theporndude|4kporno|candy\.ai|questpiracy|nudefab|nude|masturba|\bxxx\b|\bnsfw\b|\berotic|\bfetish|\bbdsm)/i;
function isAdultEntry(app: string | null, title: string | null): boolean {
  const s = `${app ?? ''} ${title ?? ''}`;
  return ADULT_RX.test(s);
}
function sanitizeWindow(w: ActivityWindow): ActivityWindow {
  const clean = w.breakdown.filter((b) => !isAdultEntry(b.app, b.title));
  if (clean.length === w.breakdown.length) return w;
  if (clean.length === 0) {
    // All entries were adult → collapse to single "Privat" placeholder.
    const totalSec = w.breakdown.reduce((s, b) => s + b.sec, 0);
    return {
      ...w,
      dominantApp: 'Privat',
      dominantTitle: null,
      breakdown: [{ app: 'Privat', title: null, sec: totalSec }],
    };
  }
  const top = clean[0];
  return { ...w, breakdown: clean, dominantApp: top.app, dominantTitle: top.title };
}

const MIN_HOUR_H = 28;
const GUTTER_W_DESKTOP = 72;
const GUTTER_W_MOBILE = 44;
const WEEKDAY_LONG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function useIsMobile() {
  const [mobile, setMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return mobile;
}

function weekStartFromDate(d: Date) {
  const nd = new Date(d);
  nd.setHours(0, 0, 0, 0);
  const dow = nd.getDay();
  const offsetToMon = dow === 0 ? -6 : 1 - dow;
  nd.setDate(nd.getDate() + offsetToMon);
  return nd.getTime();
}

function fmtHour(h: number) {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

// ─── Merge consecutive 15-min buckets that share the same label (site/app) ─────
type MergedChunk = {
  bucket: number;
  quartersCount: number;
  totalDurationMin: number;
  label: string;
  eventCount: number;
  breakdown: { app: string; title: string | null; sec: number }[];
};

function chunkLabel(w: ActivityWindow): string {
  // Use the top breakdown item (which has app+title) rather than dominantApp alone,
  // so Chrome is split by site (Claude, YouTube, LinkedIn…).
  const top = w.breakdown[0];
  if (top) return labelFor(top.app, top.title);
  return labelFor(w.dominantApp, null);
}

function mergeWindows(windows: ActivityWindow[]): MergedChunk[] {
  if (!windows.length) return [];
  const sorted = [...windows].sort((a, b) => a.bucket - b.bucket);
  const out: MergedChunk[] = [];
  let cur: MergedChunk | null = null;
  for (const w of sorted) {
    const label = chunkLabel(w);
    const adjacent = cur && Math.abs(cur.bucket + cur.quartersCount * 0.25 - w.bucket) < 0.001;
    if (cur && cur.label === label && adjacent) {
      cur.quartersCount += 1;
      cur.totalDurationMin += w.durationMin;
      cur.eventCount += w.eventCount;
      for (const b of w.breakdown) {
        const hit = cur.breakdown.find((x) => x.app === b.app && x.title === b.title);
        if (hit) hit.sec += b.sec;
        else cur.breakdown.push({ ...b });
      }
    } else {
      cur = {
        bucket: w.bucket,
        quartersCount: 1,
        totalDurationMin: w.durationMin,
        label,
        eventCount: w.eventCount,
        breakdown: w.breakdown.map((b) => ({ ...b })),
      };
      out.push(cur);
    }
  }
  return out;
}

// ─── Day View ─────────────────────────────────────────────────────────
export function ActualDayView() {
  const isMobile = useIsMobile();
  const GUTTER_W = isMobile ? GUTTER_W_MOBILE : GUTTER_W_DESKTOP;
  const {
    darkMode,
    dayOffset, goPrevDay, goNextDay, goToday,
    quietHoursEnabled, quietHoursFrom, quietHoursTo,
    zoomLevel,
    appColorOverrides,
  } = useUI();
  const { startHour: START_HOUR, endHour: END_HOUR } = visibleHourRange({ quietHoursEnabled, quietHoursFrom, quietHoursTo });
  const HOURS = END_HOUR - START_HOUR;

  const viewDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + dayOffset);
    return d;
  }, [dayOffset]);

  const weekStart = weekStartFromDate(viewDate);
  const dayIdx = (viewDate.getDay() + 6) % 7;

  const { data } = useQuery({
    queryKey: ['activity', weekStart, dayIdx],
    queryFn: () => api.getActivityWindows({ weekStart, dayIdx, bucketMin: 15 }),
    refetchInterval: 30000,
  });

  const sleepSegments = useSleepSegments(weekStart);

  const allWindows: ActivityWindow[] = data?.days?.[dayIdx] ?? [];
  const windowsInRange = useMemo(
    () => allWindows
      .filter((w) => w.bucket + 0.25 > START_HOUR && w.bucket < END_HOUR)
      .filter((w) => !isInSleep(w.bucket + 0.125, dayIdx, sleepSegments))
      .map(sanitizeWindow),
    [allWindows, START_HOUR, END_HOUR, sleepSegments, dayIdx],
  );
  const chunks = useMemo(() => mergeWindows(windowsInRange), [windowsInRange]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [baseHourH, setBaseHourH] = useState(MIN_HOUR_H);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const compute = () => {
      const avail = el.clientHeight;
      const fit = Math.floor(avail / HOURS);
      setBaseHourH(Math.max(MIN_HOUR_H, fit));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [HOURS]);

  const hourH = Math.max(MIN_HOUR_H, Math.round(baseHourH * zoomLevel));

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isToday = viewDate.getTime() === today.getTime();
  const totalH = HOURS * hourH;

  return (
    <div className="flex flex-col h-full bg-paper dark:bg-dark-paper">
      <div className="grid border-b border-line dark:border-dark-line" style={{ gridTemplateColumns: `${GUTTER_W}px 1fr` }}>
        <div />
        <div className="px-2 md:px-4 py-2 md:py-2.5 flex items-center gap-2 md:gap-3 border-l border-line dark:border-dark-line">
          <span className="text-[16px] md:text-[18px] font-semibold tracking-[-0.3px] font-display text-fg dark:text-dark-fg">
            {viewDate.getDate()}.
          </span>
          <span className="text-[12.5px] md:text-[14px] font-medium tracking-[-0.1px] text-fg dark:text-dark-fg truncate">
            {WEEKDAY_LONG[dayIdx]}
          </span>
          <span className="hidden md:inline text-[12px] font-mono text-fg dark:text-dark-fg">
            Ist · nach Website/App
          </span>
          <div className="ml-auto flex gap-1">
            <button onClick={goPrevDay} className="btn-ghost h-8 px-2" title="Tag zurück"><Icon name="chevronLeft" size={14} /></button>
            <button onClick={goToday} className="btn-ghost h-8 px-2 text-[12px]" title="Heute">Heute</button>
            <button onClick={goNextDay} className="btn-ghost h-8 px-2" title="Tag vor"><Icon name="chevronRight" size={14} /></button>
          </div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-thin">
        <div className="grid relative pt-3" style={{ gridTemplateColumns: `${GUTTER_W}px 1fr`, minHeight: '100%' }}>
          <div className="relative border-r border-line dark:border-dark-line bg-paper dark:bg-dark-paper" style={{ height: totalH }}>
            {Array.from({ length: Math.floor(HOURS) + 1 }, (_, i) => {
              const hour = Math.floor(START_HOUR) + i;
              if (hour > END_HOUR) return null;
              const top = (hour - START_HOUR) * hourH;
              return (
                <div key={hour} className="absolute left-0 right-0 flex items-center justify-end pr-1 md:pr-2 pointer-events-none" style={{ top, height: 0 }}>
                  <span className="text-[10px] md:text-[12px] font-mono text-black dark:text-white tabular-nums leading-none bg-paper dark:bg-dark-paper px-1.5 md:px-3 py-0.5">
                    {String(hour).padStart(2, '0')}:00
                  </span>
                </div>
              );
            })}
          </div>

          <div className="relative border-l border-line dark:border-dark-line" style={{ ['--cal-row-h' as any]: `${hourH}px`, height: totalH } as React.CSSProperties}>
            <div className="absolute inset-0 cal-grid-line pointer-events-none" />
            <SleepOverlay segments={sleepSegments} dayIdx={dayIdx} hourH={hourH} startHour={START_HOUR} endHour={END_HOUR} />
            {chunks.map((c) => (
              <ChunkChip
                key={`${c.bucket}-${c.quartersCount}-${c.label}`}
                c={c}
                hourH={hourH}
                startHour={START_HOUR}
                darkMode={darkMode}
                overrides={appColorOverrides}
                compact={false}
              />
            ))}
            {isToday && <NowLine startHour={START_HOUR} endHour={END_HOUR} hourH={hourH} />}
            {chunks.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-[12px] text-fg dark:text-dark-fg text-center">
                  <Icon name="clock" size={18} className="mx-auto mb-2 opacity-40" />
                  <div>Noch keine Aktivität für diesen Tag.</div>
                  <div className="text-[11px] mt-1 opacity-70">Der mac-helper schreibt alle 5 s.</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Week View ────────────────────────────────────────────────────────
export function ActualWeekView() {
  const isMobile = useIsMobile();
  const GUTTER_W = isMobile ? GUTTER_W_MOBILE : GUTTER_W_DESKTOP;
  const {
    darkMode, weekOffset,
    quietHoursEnabled, quietHoursFrom, quietHoursTo,
    zoomLevel,
    appColorOverrides,
  } = useUI();
  const { startHour: START_HOUR, endHour: END_HOUR } = visibleHourRange({ quietHoursEnabled, quietHoursFrom, quietHoursTo });
  const HOURS = END_HOUR - START_HOUR;

  const monday = useMemo(() => {
    const d = new Date();
    const dow = d.getDay();
    const delta = dow === 0 ? -6 : 1 - dow;
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + delta + weekOffset * 7);
    return d;
  }, [weekOffset]);

  const weekStart = monday.getTime();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayIdx = today >= monday && today.getTime() < monday.getTime() + 7 * 86400000 ? (today.getDay() + 6) % 7 : -1;

  const { data } = useQuery({
    queryKey: ['activity', weekStart],
    queryFn: () => api.getActivityWindows({ weekStart, bucketMin: 15 }),
    refetchInterval: 30000,
  });

  const sleepSegments = useSleepSegments(weekStart);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [baseHourH, setBaseHourH] = useState(MIN_HOUR_H);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const compute = () => {
      const avail = el.clientHeight;
      const fit = Math.floor(avail / HOURS);
      setBaseHourH(Math.max(MIN_HOUR_H, fit));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [HOURS]);
  const hourH = Math.max(MIN_HOUR_H, Math.round(baseHourH * zoomLevel));
  const totalH = HOURS * hourH;

  return (
    <div className="flex flex-col h-full bg-paper dark:bg-dark-paper">
      <div
        className="grid border-b border-line dark:border-dark-line"
        style={{ gridTemplateColumns: `${GUTTER_W}px repeat(7, 1fr)` }}
      >
        <div />
        {(isMobile ? WEEKDAY_SHORT : WEEKDAY_LONG).map((name, i) => {
          const d = new Date(monday.getTime() + i * 86400000);
          return (
            <div
              key={i}
              className="px-1 md:px-3 py-1.5 md:py-2.5 flex flex-col md:flex-row items-center md:items-baseline gap-0 md:gap-2 border-l border-line dark:border-dark-line truncate"
            >
              <span className="text-[12px] md:text-[15px] font-semibold tracking-[-0.3px] font-display text-fg dark:text-dark-fg">
                {d.getDate()}.
              </span>
              <span className="text-[10px] md:text-[12.5px] font-medium tracking-[-0.1px] truncate text-fg dark:text-dark-fg">
                {name}
              </span>
            </div>
          );
        })}
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-thin">
        <div className="grid relative pt-3" style={{ gridTemplateColumns: `${GUTTER_W}px repeat(7, 1fr)`, minHeight: '100%' }}>
          <div className="relative border-r border-line dark:border-dark-line bg-paper dark:bg-dark-paper" style={{ height: totalH }}>
            {Array.from({ length: Math.floor(HOURS) + 1 }, (_, i) => {
              const hour = Math.floor(START_HOUR) + i;
              if (hour > END_HOUR) return null;
              const top = (hour - START_HOUR) * hourH;
              return (
                <div key={hour} className="absolute left-0 right-0 flex items-center justify-end pr-1 md:pr-2 pointer-events-none" style={{ top, height: 0 }}>
                  <span className="text-[10px] md:text-[12px] font-mono text-black dark:text-white tabular-nums leading-none bg-paper dark:bg-dark-paper px-1.5 md:px-3 py-0.5">
                    {String(hour).padStart(2, '0')}:00
                  </span>
                </div>
              );
            })}
          </div>
          {Array.from({ length: 7 }, (_, di) => {
            const isToday = di === todayIdx;
            const allW = (data?.days?.[di] ?? []) as ActivityWindow[];
            const windows = allW
              .filter((w) => w.bucket + 0.25 > START_HOUR && w.bucket < END_HOUR)
              .filter((w) => !isInSleep(w.bucket + 0.125, di, sleepSegments))
              .map(sanitizeWindow);
            const chunks = mergeWindows(windows);
            return (
              <div key={di} className="relative border-l border-line dark:border-dark-line" style={{ ['--cal-row-h' as any]: `${hourH}px`, height: totalH } as React.CSSProperties}>
                <div className="absolute inset-0 cal-grid-line pointer-events-none" />
                <SleepOverlay segments={sleepSegments} dayIdx={di} hourH={hourH} startHour={START_HOUR} endHour={END_HOUR} />
                {chunks.map((c) => (
                  <ChunkChip
                    key={`${di}-${c.bucket}-${c.quartersCount}-${c.label}`}
                    c={c}
                    hourH={hourH}
                    startHour={START_HOUR}
                    darkMode={darkMode}
                    overrides={appColorOverrides}
                    compact
                  />
                ))}
                {isToday && <NowLine startHour={START_HOUR} endHour={END_HOUR} hourH={hourH} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Chip ─────────────────────────────────────────────────────────────
function ChunkChip({ c, hourH, startHour, darkMode, overrides, compact }:
  { c: MergedChunk; hourH: number; startHour: number; darkMode: boolean; overrides: Record<string, string>; compact: boolean }
) {
  const [popover, setPopover] = useState<{ x: number; y: number } | null>(null);
  const [picker, setPicker] = useState<{ x: number; y: number } | null>(null);

  const top = (c.bucket - startHour) * hourH;
  const capacityH = c.quartersCount * 0.25 * hourH;
  const height = Math.max(10, capacityH) - 1;
  const tone = toneForLabel(c.label, overrides);
  const bg = darkMode ? tone.bgDark : tone.bg;
  const ink = darkMode ? tone.inkDark : tone.ink;
  const endHour = c.bucket + c.quartersCount * 0.25;

  const fontPx = height >= 56 ? 12 : height >= 36 ? 11 : height >= 20 ? 10 : 9;
  const showTime = !compact && height >= 18;
  const showMin = !compact && height >= 28;

  return (
    <>
      <div
        className={cn(
          'absolute rounded-[4px] overflow-hidden cursor-pointer',
          compact ? 'left-0.5 right-0.5 px-1' : 'left-2 right-2 px-2',
        )}
        style={{
          top, height,
          background: bg,
          color: ink,
        }}
        title={`${c.label} · ${fmtHour(c.bucket)}–${fmtHour(endHour)} · ${c.totalDurationMin} min · Rechtsklick = Farbe`}
        onClick={(e) => setPopover({ x: e.clientX, y: e.clientY })}
        onContextMenu={(e) => {
          e.preventDefault();
          setPicker({ x: e.clientX, y: e.clientY });
        }}
      >
        <div
          className="absolute inset-0 flex items-center justify-center gap-1.5 px-1 leading-tight font-medium whitespace-nowrap overflow-hidden"
          style={{ fontSize: `${fontPx}px` }}
        >
          {showTime && <span className="font-mono opacity-70 tabular-nums">{fmtHour(c.bucket)}</span>}
          <span className="truncate">{c.label}</span>
          {showMin && <span className="font-mono opacity-60 tabular-nums">· {c.totalDurationMin}m</span>}
        </div>
      </div>
      {popover && <ChunkPopover c={c} x={popover.x} y={popover.y} onClose={() => setPopover(null)} tone={tone} darkMode={darkMode} />}
      {picker && <ColorPicker label={c.label} x={picker.x} y={picker.y} onClose={() => setPicker(null)} />}
    </>
  );
}

// ─── Popover (details) ────────────────────────────────────────────────
function ChunkPopover({ c, x, y, onClose, tone, darkMode }: { c: MergedChunk; x: number; y: number; onClose: () => void; tone: AppTone; darkMode: boolean }) {
  const total = c.breakdown.reduce((sum, b) => sum + b.sec, 0) || 1;
  const endHour = c.bucket + c.quartersCount * 0.25;
  const sorted = [...c.breakdown].sort((a, b) => b.sec - a.sec);

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        className="fixed z-50 rounded-lg shadow-pop w-[360px] overflow-hidden bg-paper dark:bg-dark-paper border border-line dark:border-dark-line"
        style={{ left: Math.min(x, window.innerWidth - 380), top: Math.min(y, window.innerHeight - 300) }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-3 py-2 border-b border-line dark:border-dark-line flex items-center gap-2" style={{ background: darkMode ? tone.bgDark : tone.bg, color: darkMode ? tone.inkDark : tone.ink }}>
          <div className="font-semibold text-[13px] tracking-[-0.1px]">{c.label}</div>
          <div className="font-mono text-[11px] tabular-nums opacity-80">{fmtHour(c.bucket)} – {fmtHour(endHour)}</div>
          <div className="ml-auto text-[11px] font-medium">{c.totalDurationMin} min · {c.eventCount} Events</div>
        </div>
        <div className="px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-fg dark:text-dark-fg mb-1.5">Was genau</div>
          <div className="space-y-1 max-h-[240px] overflow-y-auto scroll-thin">
            {sorted.map((b, i) => {
              const pct = Math.round((b.sec / total) * 100);
              return (
                <div key={i} className="flex items-center gap-2 text-[12px]">
                  <div className="flex-1 min-w-0 truncate text-fg dark:text-dark-fg">
                    {b.title ?? b.app}
                  </div>
                  <div className="font-mono text-[10.5px] tabular-nums text-fg dark:text-dark-fg shrink-0 w-10 text-right">
                    {Math.round(b.sec / 60)}m
                  </div>
                  <div className="font-mono text-[10.5px] tabular-nums text-fg dark:text-dark-fg shrink-0 w-8 text-right opacity-60">
                    {pct}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Color Picker (right-click) ───────────────────────────────────────
function ColorPicker({ label, x, y, onClose }: { label: string; x: number; y: number; onClose: () => void }) {
  const { appColorOverrides, setAppColorOverride, resetAppColor, darkMode } = useUI();
  const currentId = toneForLabel(label, appColorOverrides).id;

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
      <div
        className="fixed z-50 rounded-lg shadow-pop w-[252px] overflow-hidden bg-paper dark:bg-dark-paper border border-line dark:border-dark-line"
        style={{ left: Math.min(x, window.innerWidth - 272), top: Math.min(y, window.innerHeight - 200) }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-3 py-2 border-b border-line dark:border-dark-line flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-fg dark:text-dark-fg">Farbe</span>
          <span className="text-[12.5px] font-semibold text-fg dark:text-dark-fg truncate">{label}</span>
        </div>
        <div className="p-2 grid grid-cols-6 gap-1.5">
          {APP_PALETTE.map((t) => {
            const active = t.id === currentId;
            return (
              <button
                key={t.id}
                onClick={() => { setAppColorOverride(label, t.id); onClose(); }}
                className={cn(
                  'h-8 rounded-md border transition-transform hover:scale-[1.08]',
                  active ? 'ring-2 ring-offset-1 ring-offset-paper dark:ring-offset-dark-paper ring-accent' : 'border-transparent',
                )}
                style={{ background: darkMode ? t.bgDark : t.bg, borderColor: t.line }}
                title={t.name}
              />
            );
          })}
        </div>
        {appColorOverrides[label] && (
          <div className="px-2 pb-2">
            <button
              onClick={() => { resetAppColor(label); onClose(); }}
              className="w-full h-7 text-[11.5px] rounded-md border border-line dark:border-dark-line text-fg dark:text-dark-fg hover:bg-paperAlt dark:hover:bg-dark-paperAlt"
            >
              Zurücksetzen auf Standard
            </button>
          </div>
        )}
      </div>
    </>
  );
}
